import React, { useState, useEffect } from 'react';
import { MapPin, Users, Calendar, Pencil, Trash2, Phone, Mail, User, Search, Check, X, FileText, Save } from 'lucide-react';
import NewLocationModal from '../components/NewLocationModal';
import { supabase } from '../lib/supabase';
import { eventApi } from '../lib/api';
import type { Tables } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { ITALIAN_PROVINCES } from '../constants/cities';

const Locations = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingLocation, setViewingLocation] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    loadLocationsWithNextEvent();
  }, []);

  const loadLocationsWithNextEvent = async () => {
    try {
      setLoading(true);
      
      // Carica le locations da Supabase
      let query = supabase.from('locations').select('*').order('city, name');

      // Filter based on user role and city
      if (user?.role === 'organizer') {
        query = query.eq('city', user.city);
      }

      const { data: locations, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Carica gli eventi da Supabase
      const events = await eventApi.getAll();
      
      // Filtra gli eventi futuri e li ordina per data
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Resetta l'ora a mezzanotte per confrontare solo le date

      const futureEvents = events
        .filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= today;
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Aggiorna le location con il prossimo evento
      const locationsWithNextEvent = (locations || []).map((location: any) => {
        const nextEvent = futureEvents.find(event => event.location === location.name);
        const eventsHosted = events.filter(event => event.location === location.name).length;

        return {
          ...location,
          eventsHosted,
          nextEvent: nextEvent ? {
            name: nextEvent.name,
            date: nextEvent.date,
            time: nextEvent.time
          } : null
        };
      });

      setLocations(locationsWithNextEvent);
    } catch (error) {
      console.error('Error loading locations:', error);
      alert('Errore nel caricamento delle location');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\./g, '/');
  };

  const handleNewLocation = async (locationData: any) => {
    try {
      const locationPayload = {
        name: locationData.name,
        address: locationData.address,
        city: locationData.city,
        capacity: parseInt(locationData.capacity),
        contact_name: locationData.contactName,
        contact_phone: locationData.contactPhone,
        contact_email: locationData.contactEmail,
        image_url: locationData.imageUrl || null,
        image_position_x: locationData.imagePositionX ?? 50,
        image_position_y: locationData.imagePositionY ?? 50,
        status: 'active'
      };

      if (editingLocation) {
        const { error } = await supabase
          .from('locations')
          .update(locationPayload)
          .eq('id', editingLocation.id);

        if (error) {
          console.error('Supabase update error:', error);
          throw error;
        }
      } else {
        const { error } = await supabase
          .from('locations')
          .insert(locationPayload);

        if (error) {
          console.error('Supabase insert error:', error);
          throw error;
        }
      }

      await loadLocationsWithNextEvent();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving location:', error);
      alert(`Errore nel salvare la location: ${error.message || 'Errore sconosciuto'}`);
    }
  };

  const handleEdit = (location: any) => {
    const cleanLocation = {
      id: location.id,
      name: location.name,
      address: location.address,
      city: location.city,
      capacity: location.capacity,
      contact_name: location.contact_name,
      contact_phone: location.contact_phone,
      contact_email: location.contact_email,
      status: location.status,
      created_at: location.created_at,
      notes: location.notes,
      image_url: location.image_url,
      image_position_x: location.image_position_x ?? 50,
      image_position_y: location.image_position_y ?? 50
    };
    setEditingLocation(cleanLocation);
    setIsModalOpen(true);
  };

  const handleDelete = async (locationId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa location?')) {
      try {
        const { error } = await supabase
          .from('locations')
          .delete()
          .eq('id', locationId);

        if (error) {
          console.error('Supabase delete error:', error);
          throw error;
        }
        
        await loadLocationsWithNextEvent();
      } catch (error: any) {
        console.error('Error deleting location:', error);
        alert(`Errore nell'eliminazione della location: ${error.message || 'Errore sconosciuto'}`);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLocation(null);
  };

  const handleViewLocation = (location: any) => {
    setViewingLocation(location);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingLocation(null);
  };

  const handleUpdateNotes = async (notes: string) => {
    if (!viewingLocation) return;

    try {
      const { error } = await supabase
        .from('locations')
        .update({ notes })
        .eq('id', viewingLocation.id);

      if (error) throw error;

      await loadLocationsWithNextEvent();
      setViewingLocation({ ...viewingLocation, notes });
    } catch (error: any) {
      console.error('Error updating notes:', error);
      alert(`Errore nell'aggiornamento delle note: ${error.message || 'Errore sconosciuto'}`);
    }
  };

  const filteredLocations = locations.filter(location => {
    const searchLower = searchQuery.toLowerCase();
    return (
      location.name.toLowerCase().includes(searchLower) ||
      location.address.toLowerCase().includes(searchLower) ||
      location.city.toLowerCase().includes(searchLower) ||
      location.contact_name.toLowerCase().includes(searchLower) ||
      location.contact_email.toLowerCase().includes(searchLower) ||
      location.contact_phone.toLowerCase().includes(searchLower)
    );
  });

  // Group locations by city
  const locationsByCity = ITALIAN_PROVINCES.reduce((acc, city) => {
    acc[city] = filteredLocations.filter(location => location.city === city);
    return acc;
  }, {} as Record<string, typeof filteredLocations>);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-gray-500">Caricamento locations...</div>
      </div>
    );
  }

  const LocationTable = ({ locations }: { locations: any[] }) => (
    <table className="w-full table-fixed">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[20%]">Nome</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[25%]">Indirizzo</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[8%]">Capacità</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[10%]">Eventi Ospitati</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[22%]">Prossimo Evento</th>
          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[8%]">Status</th>
          {user?.role === 'admin' && (
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 w-[7%]">Azioni</th>
          )}
        </tr>
      </thead>
      <tbody>
        {locations.map((location) => (
          <tr key={location.id} className="border-b border-gray-100">
            <td className="py-3 px-4 truncate">
              <button
                onClick={() => handleViewLocation(location)}
                className="text-rose-600 hover:text-rose-700 hover:underline font-medium text-left truncate max-w-full"
              >
                {location.name}
              </button>
            </td>
            <td className="py-3 px-4 truncate">{location.address}</td>
            <td className="py-3 px-4">{location.capacity}</td>
            <td className="py-3 px-4">{location.eventsHosted}</td>
            <td className="py-3 px-4">
              {location.nextEvent ? (
                <div className="space-y-1">
                  <div className="font-medium truncate">{location.nextEvent.name}</div>
                  <div className="text-sm text-gray-500">
                    {formatDate(location.nextEvent.date)} - {location.nextEvent.time}
                  </div>
                </div>
              ) : (
                <span className="text-gray-400">Nessun evento programmato</span>
              )}
            </td>
            <td className="py-3 px-4">
              <span className="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
                {location.status}
              </span>
            </td>
            {user?.role === 'admin' && (
              <td className="py-3 px-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(location)}
                    className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                    title="Modifica"
                  >
                    <Pencil className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(location.id)}
                    className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                    title="Elimina"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const totalLocations = locations.length;
  const activeLocations = locations.filter(loc => loc.status === 'active').length;
  const totalCapacity = locations.reduce((sum, loc) => sum + (loc.capacity || 0), 0);
  const locationsWithEvents = locations.filter(loc => loc.nextEvent).length;

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Location</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalLocations}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Location Attive</p>
                  <p className="text-3xl font-bold text-white mt-1">{activeLocations}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Check className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Capacità Totale</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalCapacity}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Con Eventi</p>
                  <p className="text-3xl font-bold text-white mt-1">{locationsWithEvents}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Cerca locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
          />
        </div>
        {(user?.role === 'admin' || user?.role === 'organizer') && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition-all flex items-center gap-2 text-sm font-medium shadow-sm flex-shrink-0"
          >
            <MapPin className="h-4 w-4" />
            Nuova Location
          </button>
        )}
      </div>

      <div className="space-y-8">
        {ITALIAN_PROVINCES.map(city => {
          const cityLocations = locationsByCity[city];
          if (!cityLocations?.length) return null;

          return (
            <div key={city} className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-rose-500" />
                  <h2 className="text-lg font-semibold">{city}</h2>
                  <span className="text-sm text-gray-500">
                    ({cityLocations.length} location{cityLocations.length !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <LocationTable locations={cityLocations} />
                </div>
              </div>
            </div>
          );
        })}

        {filteredLocations.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Nessuna location trovata</p>
          </div>
        )}
      </div>

      <NewLocationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleNewLocation}
        initialData={editingLocation}
      />

      {viewingLocation && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 ${
            isViewModalOpen ? '' : 'hidden'
          }`}
          onClick={handleCloseViewModal}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-br from-rose-500 to-pink-600 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <MapPin className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Dettagli Location</h2>
              </div>
              <button
                onClick={handleCloseViewModal}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {viewingLocation.image_url && (
                <div className="w-full">
                  <img
                    src={viewingLocation.image_url}
                    alt={viewingLocation.name}
                    className="w-full h-64 object-cover rounded-lg"
                    style={{
                      objectPosition: `${viewingLocation.image_position_x ?? 50}% ${viewingLocation.image_position_y ?? 50}%`
                    }}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Nome</label>
                  <p className="text-gray-900 font-semibold">{viewingLocation.name}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Città</label>
                  <p className="text-gray-900">{viewingLocation.city}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Indirizzo</label>
                  <p className="text-gray-900">{viewingLocation.address}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Capacità</label>
                  <p className="text-gray-900">{viewingLocation.capacity} persone</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                  <span className="inline-flex px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
                    {viewingLocation.status}
                  </span>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Eventi Ospitati</label>
                  <p className="text-gray-900">{viewingLocation.eventsHosted}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Prossimo Evento</label>
                  {viewingLocation.nextEvent ? (
                    <div>
                      <p className="text-gray-900 font-medium">{viewingLocation.nextEvent.name}</p>
                      <p className="text-sm text-gray-600">
                        {formatDate(viewingLocation.nextEvent.date)} - {viewingLocation.nextEvent.time}
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-400">Nessun evento programmato</p>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-rose-600" />
                  Referente
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Nome</label>
                    <p className="text-gray-900">{viewingLocation.contact_name}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefono
                    </label>
                    <p className="text-gray-900">
                      <a href={`tel:${viewingLocation.contact_phone}`} className="text-rose-600 hover:text-rose-700 hover:underline">
                        {viewingLocation.contact_phone}
                      </a>
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </label>
                    <p className="text-gray-900">
                      <a href={`mailto:${viewingLocation.contact_email}`} className="text-rose-600 hover:text-rose-700 hover:underline">
                        {viewingLocation.contact_email}
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-rose-600" />
                  Note
                </h3>
                <NotesSection
                  notes={viewingLocation.notes || ''}
                  onUpdate={handleUpdateNotes}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NotesSection = ({ notes, onUpdate }: { notes: string; onUpdate: (notes: string) => void }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(notes);

  const handleSave = async () => {
    await onUpdate(editedNotes);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedNotes(notes);
    setIsEditing(false);
  };

  useEffect(() => {
    setEditedNotes(notes);
  }, [notes]);

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={editedNotes}
            onChange={(e) => setEditedNotes(e.target.value)}
            rows={5}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
            placeholder="Aggiungi note su questa location..."
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Salva
            </button>
          </div>
        </div>
      ) : (
        <div>
          {notes ? (
            <p className="text-gray-900 whitespace-pre-wrap mb-3">{notes}</p>
          ) : (
            <p className="text-gray-400 mb-3">Nessuna nota</p>
          )}
          <button
            onClick={() => setIsEditing(true)}
            className="text-rose-600 hover:text-rose-700 text-sm font-medium"
          >
            {notes ? 'Modifica note' : 'Aggiungi note'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Locations;